import React, { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import {
  HStack,
  useToast,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Checkbox,
  Switch,
} from '@chakra-ui/react';
import {
  FieldError,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import context from '../context';
import cms from '../../../apis/cms';
import Input from '../../Form/Input';
import Datepicker from '../../Form/Datepicker';
import Upload from '../../Form/Upload';

import {
  CREATE_GROUP_ELEMENT,
  REMOVE_GROUP_ELEMENT,
  UPDATE_GROUP_ELEMENT,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import Textarea from '../../Form/Textarea';

interface FormData {
  name: string;
  countdown: boolean;
  schedule: boolean;
  description?: string;
  schedule_start?: Date;
  schedule_end?: Date;
  file?: FileList;
}

const formData = yup.object().shape({
  name: yup.string().required('El slug es obligatorio'),
  description: yup.string().optional().nullable(),
  schedule: yup.boolean(),
  countdown: yup.boolean(),
  schedule_start: yup.date().when('schedule', {
    is: true,
    then: yup.date().required('La fecha de inicio es obligatoria'),
  }),
  schedule_end: yup
    .date()
    .when(['schedule', 'schedule_start'], (schedule, schedule_start) => {
      if (!schedule) {
        return yup.date();
      }
      if (schedule_start) {
        return yup
          .date()
          .min(
            schedule_start,
            'La fecha final debe ser posterior a la fecha de inicio',
          )
          .required('La fecha final es obligatoria');
      }
      return yup.date().required('La fecha final es obligatoria');
    }),
});

interface Props {
  onClose: () => void;
  onCreated?: () => void;
  onUpdated?: () => void;
  onDeleted?: () => void;
  isOpen: boolean;
  banner?: any;
}

const BannerForm: React.FC<Props> = ({
  onClose,
  isOpen,
  onCreated,
  banner,
  onUpdated,
  onDeleted,
}) => {
  const toast = useToast();

  const { pageGroup, element } = useContext(context);

  const {
    control,
    register,
    handleSubmit,
    formState,
    reset,
    setValue,
    setError,
  } = useForm({
    resolver: yupResolver(formData),
  });

  const [createGroupElement] = useMutation(CREATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });
  const [updateGroupElement] = useMutation(UPDATE_GROUP_ELEMENT, {
    refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
  });
  const [removeGroupElement, { loading: removeLoading, error: removeError }] =
    useMutation(REMOVE_GROUP_ELEMENT, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const { errors } = formState;

  const [schedule, setSchedule] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    if (!banner) return;
    setValue('name', banner.name);
    setValue('description', banner.description);
    setValue('countdown', banner.countdown);

    if (banner.schedule_end) {
      setSchedule(true);
      setValue('schedule', true);
      setValue('schedule_start', new Date(banner.schedule_start));
      setValue('schedule_end', new Date(banner.schedule_end));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner, setValue]);

  useEffect(() => {
    if (removeError) {
      toast({
        title: 'Opss!!',
        description: removeError?.message || t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    }
  }, [removeError, toast, t]);

  const handleDelete = async () => {
    try {
      const confirmed = window.confirm(t('confirmBannerDeletion'));
      if (confirmed) {
        await removeGroupElement({
          variables: {
            id: banner?.id,
          },
        });
        if (onDeleted) onDeleted();
        reset();
        setSchedule(false);
        toast({
          title: t('success'),
          description: t('bannerDeletedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      }
    } catch {
      // error
    }
  };

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      setLoading(true);
      const { file, schedule: scheduleData, ...rest } = data;

      if (!file?.length && !banner?.file.id) {
        setError('file', {
          type: 'manual',
          message: t('insertImage'),
        });
        return;
      }

      if (banner) {
        let uploadData: any = null;
        const variables: any = {
          updateGroupElementInput: {
            ...rest,
            id: banner?.id,
            schedule_end: scheduleData ? rest.schedule_end : null,
            countdown: scheduleData ? rest.countdown : false,
          },
        };
        if (file && file[0]) {
          const multipart = new FormData();
          multipart.append('file', file[0]);
          const { data: uploadDataFromApi } = await cms.rest.post<any>(
            '/upload',
            multipart,
          );
          uploadData = uploadDataFromApi;
        }
        if (uploadData) {
          variables.updateGroupElementInput.file_id = uploadData.id;
        }

        await updateGroupElement({
          variables,
        });
        if (onUpdated) onUpdated();
        reset();
        setSchedule(false);
        toast({
          title: t('success'),
          description: t('bannerEditedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      } else {
        let uploadDataCreate: any = null;
        if (file && file[0]) {
          const multipart = new FormData();
          multipart.append('file', file[0]);
          const { data: uploadData } = await cms.rest.post<any>(
            '/upload',
            multipart,
          );
          uploadDataCreate = uploadData;
        }
        const bodyCreate: any = {
          ...rest,
          page_group_id: pageGroup?.id,
          element_id: element?.id,
          file_id: uploadDataCreate?.id || null,
        };

        await createGroupElement({
          variables: {
            createGroupElementInput: bodyCreate,
          },
        });
        if (onCreated) onCreated();
        reset();
        setSchedule(false);

        toast({
          title: t('success'),
          description: t('bannerCreatedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      }
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description:
          err?.response?.data?.message || err?.message || t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      onClose={() => {
        reset();
        setSchedule(false);
        onClose();
      }}
      isOpen={isOpen}
      size="lg"
    >
      <DrawerOverlay zIndex={1_400} />
      <DrawerContent>
        <DrawerHeader>{t('newPreference')}</DrawerHeader>
        <DrawerBody
          display="flex"
          justifyContent="space-between"
          w="full"
          gridGap="4"
        >
          <VStack
            as="form"
            spacing="4"
            onSubmit={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
            w="60%"
          >
            <Input
              mt={0}
              label={t('nameField')}
              isRequired
              error={errors.name as FieldError | undefined}
              {...register('name')}
            />
            <Textarea
              label={t('descriptionField')}
              error={errors.description as FieldError | undefined}
              {...register('description')}
            />
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="schedule" mb="0">
                {t('schedule')}
              </FormLabel>
              <Switch
                id="schedule"
                size="lg"
                colorScheme="brand"
                isChecked={schedule}
                {...register('schedule')}
                onChange={() => setSchedule(state => !state)}
              />
            </FormControl>
            {schedule && (
              <>
                <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
                  <Datepicker
                    isRequired
                    control={control}
                    name="schedule_start"
                    label={t('scheduleStart')}
                    showTimeSelect
                    error={errors.schedule_start as FieldError | undefined}
                  />
                  <Datepicker
                    isRequired
                    control={control}
                    name="schedule_end"
                    label={t('scheduleEnd')}
                    showTimeSelect
                    error={errors.schedule_end as FieldError | undefined}
                  />
                </SimpleGrid>
                <Checkbox
                  alignSelf="flex-start"
                  colorScheme="brand"
                  {...register('countdown')}
                >
                  {t('countdown')}
                </Checkbox>
              </>
            )}
          </VStack>
          <Upload
            label={t('image')}
            mb="10"
            acceptedFileTypes="image/*"
            alignSelf="center"
            error={errors.file as FieldError | undefined}
            url={banner?.file?.url}
            {...register('file')}
          />
        </DrawerBody>
        <DrawerFooter>
          <HStack spacing={4}>
            {!!banner && (
              <Button
                isLoading={removeLoading || loading}
                colorScheme="red"
                onClick={handleDelete}
              >
                {t('remove')}
              </Button>
            )}
            <Button
              isLoading={removeLoading || loading}
              colorScheme="brand"
              onClick={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
            >
              {t('save')}
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default BannerForm;
